import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { subscriptionApi } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { isOwner } from '../../utils/roles';
import { SettingsGrid, SettingsCard, SettingRow, Tag, PreviewPanel, Field, inputClass, buttonClass } from './SettingsUI';
import { CreditCardIcon, MailIcon, PlusIcon, XIcon, AlertTriangleIcon, GiftIcon } from '../icons/LucideIcons';
import {
  formatMoney, formatDate, graceDaysLeft, itemPriceCents, totalCents, yearlySavingsCents,
  availableAddOns, toggleAddOn, addOnsForPlan, perInterval, switchPlanOptions, foundingDiscountCents,
} from './planBilling';

/**
 * Settings → Plan & billing (billing B3), from GET /api/billing.
 *
 * Owners act; admins see the same screen read-only (the backend enforces it:
 * every action route is owner-only). Hidden entirely for billing-exempt
 * shops (Twigs): the summary is { billingExempt: true } and this renders
 * nothing (SettingsDrawer also hides the tab).
 *
 *  - Your plan: plan, interval, renewal date, add-ons, status (past-due grace
 *    days left, suspended), "Manage card & invoices" (Stripe portal).
 *  - Tracked orders: the month's usage against the texting allowance and the
 *    overage rate.
 *  - Add-ons: add or remove, priced first from Stripe's upcoming invoice
 *    (POST /billing/add-ons/preview), then made at that proration date.
 *  - Change your plan (B3b, live subscription): plan (Core / Everything) and
 *    monthly/yearly, priced first by Stripe's invoice preview
 *    (POST /billing/switch/preview), then made at that price; a changed
 *    price comes back 409 PREVIEW_CHANGED and is shown again. Monthly to
 *    yearly is charged now; yearly to monthly waits for the end of the paid
 *    year (shown, with a way to keep the current plan). The Customer Portal
 *    can't change a subscription with more than one product, so it stays for
 *    card, invoices and cancel.
 *  - Choose a plan (no live subscription: a trial, a suspended shop): plan,
 *    add-ons and the monthly/yearly toggle with what yearly saves, then
 *    Stripe Checkout. A suspended shop's button says "Reactivate". A shop
 *    granted the founding offer (B6) starts on Core yearly with the founding
 *    discount in the total, and checks out with it. A shop the server counts
 *    as switching from Hana sees the one-time $299 switch fee line before
 *    checkout (waived on yearly). "Have a code?" checks a discount code with
 *    Stripe's preview (POST /billing/code/preview) and checks out with it; a
 *    code Bouqmark applied for the shop is named (2026-10-18).
 *  - Yearly plans: cancel with the refund shown before confirming.
 *
 * `billingOnly` (BillingOnlyScreen): the owner of a suspended shop signed in
 * to a billing-only session. After checkout it refreshes the session until
 * the webhook has reactivated the shop, which reopens the full app.
 * `onLoaded(data)`: the GET /api/billing summary, each time it loads (the
 * billing-only screen words its explainer from it).
 *
 * QA fix round (2026-10-17): an unpaid bill offers "Pay the bill" (Stripe's
 * hosted invoice, POST /billing/pay-now) beside "Update card" (the portal);
 * a trial checkout says nothing was charged; an add-on priced during the
 * trial shows the first real bill; a shop granted the founding offer sees it
 * held for its switch to Core yearly; trialing shops learn that customers get
 * no texts until the plan is paid, and that the shop's own number then takes
 * the carriers a few days.
 */

export const PLAN_CONTACT_EMAIL = 'hello@bouqmark.com';
const CONTACT_HREF = `mailto:${PLAN_CONTACT_EMAIL}?subject=${encodeURIComponent('Change my plan')}`;
const LIVE = ['trialing', 'active', 'past_due'];
const POLL_MS = 3000;
const POLL_TRIES = 20;

const errorText = (err, fallback) => (err && err.message) || fallback;

const ContactLink = () => (
  <a href={CONTACT_HREF} className={`${buttonClass.secondary} self-start`}>
    <MailIcon size={16} />
    Contact us to change your plan
  </a>
);

const Alert = ({ children }) => (
  <p className="text-[13px] text-danger-fg" role="alert">{children}</p>
);

function statusOf({ sub, shopStatus, trialEndsAt }) {
  if (shopStatus === 'suspended') {
    return {
      tag: <Tag tone="warn">Suspended</Tag>,
      text: sub && sub.status === 'past_due'
        ? "Your payments didn't go through, so your shop is closed. Pay the bill or update your card to reopen it. Your data is kept."
        : 'Your plan has ended, so your shop is closed. Your data is kept. Choose a plan to reactivate.',
    };
  }
  if (sub && sub.status === 'past_due') {
    const days = graceDaysLeft(sub.graceEndsAt);
    return {
      tag: <Tag tone="warn">Payment overdue</Tag>,
      text: `Your last payment didn't go through. Pay the bill or update your card within ${days} ${days === 1 ? 'day' : 'days'} to keep your shop open.`,
    };
  }
  if (sub && LIVE.includes(sub.status)) {
    const date = formatDate(sub.currentPeriodEnd);
    let text = null;
    if (sub.cancelAtPeriodEnd) text = date ? `Ends on ${date}.` : null;
    else if (sub.status === 'trialing') text = date ? `Your first payment is on ${date}.` : null;
    else text = date ? `Renews on ${date}.` : null;
    return { tag: <Tag tone="ok">Active</Tag>, text };
  }
  if (shopStatus === 'trial') {
    const date = formatDate(trialEndsAt);
    return { tag: <Tag tone="neutral">Free trial</Tag>, text: date ? `Your free trial ends on ${date}.` : null };
  }
  return { tag: null, text: null };
}

function AddOnList({ addOns }) {
  if (!addOns || addOns.length === 0) return <span className="text-[13px] text-gray-600">None</span>;
  return (
    <ul className="flex flex-col items-end gap-1 text-[13px] text-gray-900" data-testid="plan-add-ons">
      {addOns.map((a) => (
        <li key={a.key}>{a.name}{a.quantity > 1 ? ` × ${a.quantity}` : ''}</li>
      ))}
    </ul>
  );
}

// A bill is unpaid: the plan past due, or the overage bill (pastDue, B5b).
const owesMoney = (data) => Boolean(data.pastDue || (data.subscription && data.subscription.status === 'past_due'));
// No customer texts while trialing (trial texting preview mode).
const trialing = (data) => data.shopStatus === 'trial' || Boolean(data.subscription && data.subscription.status === 'trialing');

function YourPlanCard({ data, owner, onPortal, portalBusy, portalError, onPayNow, payBusy, payError }) {
  const { subscription: sub, assignedPlan, catalog } = data;
  const owes = owesMoney(data);
  const status = statusOf({ sub, shopStatus: data.shopStatus, trialEndsAt: data.trialEndsAt });
  const shown = sub || assignedPlan;
  const interval = shown && shown.interval;
  const addOnKeys = ((shown && shown.addOns) || []).map((a) => ({ key: a.key, quantity: a.quantity }));
  const savings = sub && LIVE.includes(sub.status) && interval === 'month' && sub.plan
    ? yearlySavingsCents(catalog, sub.plan.key, addOnKeys) : 0;

  return (
    <SettingsCard title="Your plan" description={status.text}>
      {shown ? (
        <>
          <SettingRow
            title={(shown.plan && shown.plan.name) || 'Your plan'}
            description={interval === 'year' ? 'Billed yearly' : interval === 'month' ? 'Billed monthly' : null}
          >
            {shown.founding && <Tag tone="ok">Founding shop</Tag>}
            {status.tag}
          </SettingRow>
          <SettingRow title="Add-ons" divider>
            <AddOnList addOns={shown.addOns} />
          </SettingRow>
        </>
      ) : (
        <SettingRow title={data.shopStatus === 'trial' ? 'Every feature is on during your trial' : "There's no plan on file for your shop."}>
          {status.tag}
        </SettingRow>
      )}
      {savings > 0 && (
        <p className="text-[13px] text-gray-700" data-testid="yearly-savings">
          Paying yearly saves {formatMoney(savings)} a year (two months free).{owner ? ' Switch under Change your plan.' : ''}
        </p>
      )}
      {assignedPlan && !sub && (
        <>
          <p className="text-[13px] text-gray-700">Our team set this plan up for you.</p>
          <ContactLink />
        </>
      )}
      {trialing(data) && (
        <p className="text-[13px] text-gray-700" data-testid="trial-texting-note">
          During your free trial customers get no texts: order confirmation emails and receipts carry the tracking link, and you can
          send test texts to your own phone. Your shop&apos;s own texting number is registered once your plan is paid; the phone
          carriers take a few days to approve it.
        </p>
      )}
      {owner && owes && data.hasBillingAccount && (
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={onPayNow} disabled={payBusy} className={buttonClass.primary} data-testid="pay-now">
            {payBusy ? 'Opening…' : 'Pay the bill'}
          </button>
          <button type="button" onClick={onPortal} disabled={portalBusy} className={buttonClass.secondary}>
            <CreditCardIcon size={16} />
            {portalBusy ? 'Opening…' : 'Update card'}
          </button>
        </div>
      )}
      {owner && !owes && data.hasBillingAccount && sub && (
        <button type="button" onClick={onPortal} disabled={portalBusy} className={`${buttonClass.secondary} self-start`}>
          <CreditCardIcon size={16} />
          {portalBusy ? 'Opening…' : 'Manage card & invoices'}
        </button>
      )}
      {payError && <Alert>{payError}</Alert>}
      {portalError && <Alert>{portalError}</Alert>}
      {!owner && (
        <p className="text-[13px] text-gray-600" data-testid="owner-only-note">
          Only the shop&apos;s owner can change the plan or billing.
        </p>
      )}
    </SettingsCard>
  );
}

function UsageCard({ usage, rateCents }) {
  if (!usage || usage.included == null) return null;
  const used = Number(usage.trackedOrders) || 0;
  const included = Number(usage.included) || 0;
  const pct = included > 0 ? Math.min(100, Math.round((used / included) * 100)) : 100;
  const over = Number(usage.overage) || 0;
  return (
    <SettingsCard title="Tracked orders" description="Orders whose customer got delivery texts this month. Holiday weeks don't count.">
      <p className="text-sm font-semibold text-gray-900" data-testid="plan-usage">
        {used} of {included} tracked orders this month
      </p>
      <div
        className="h-1.5 w-full overflow-hidden rounded-full bg-floral-100"
        role="meter"
        aria-label="Tracked orders used"
        aria-valuemin={0}
        aria-valuemax={included}
        aria-valuenow={Math.min(used, included)}
      >
        <div className={`h-full rounded-full ${used > included ? 'bg-floral-800' : 'bg-floral-700'}`} style={{ width: `${pct}%` }} />
      </div>
      <p className="text-[13px] text-gray-700" data-testid="plan-overage">
        After {included}, each tracked order is {formatMoney(rateCents)}. Texts are never stopped.
        {over > 0 && ` ${over} over so far this month: ${formatMoney(over * rateCents)}.`}
      </p>
    </SettingsCard>
  );
}

function AddOnsCard({ data, onChanged }) {
  const { subscription: sub, catalog } = data;
  const interval = sub.interval;
  const current = (sub.addOns || []).map((a) => ({ key: a.key, quantity: a.quantity }));
  const currentKeys = current.map((a) => a.key);
  const [pending, setPending] = useState(null); // { label, addOns, preview }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const ask = async (key) => {
    const nextKeys = toggleAddOn(catalog, currentKeys, key);
    const nextAddOns = nextKeys.map((k) => current.find((a) => a.key === k) || { key: k, quantity: 1 });
    const item = catalog.addOns.find((a) => a.key === key);
    const adding = !currentKeys.includes(key);
    setBusy(true);
    setError('');
    try {
      const r = await subscriptionApi.previewAddOns(nextAddOns);
      setPending({ label: `${adding ? 'Add' : 'Remove'} ${item ? item.name : key}`, addOns: nextAddOns, preview: r.data });
    } catch (err) {
      setError(errorText(err, "Couldn't price that change. Try again."));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    setError('');
    try {
      const r = await subscriptionApi.changeAddOns(pending.addOns, pending.preview.prorationDate);
      setPending(null);
      onChanged(r.data);
    } catch (err) {
      setError(errorText(err, "Couldn't change your add-ons. Try again."));
    } finally {
      setBusy(false);
    }
  };

  const options = availableAddOns(catalog, sub.plan && sub.plan.key);
  if (options.length === 0) return null;
  const p = pending && pending.preview;

  return (
    <SettingsCard title="Add-ons" description="Changes are prorated: you pay only for the rest of this billing period.">
      {options.map((a, i) => {
        const on = currentKeys.includes(a.key);
        const qty = (current.find((c) => c.key === a.key) || {}).quantity || 1;
        return (
          <SettingRow
            key={a.key}
            title={`${a.name}${on && qty > 1 ? ` × ${qty}` : ''}`}
            description={`${formatMoney(itemPriceCents(catalog, a.key, interval))}/${perInterval(interval)}${a.includedTrackedOrders ? ` · ${a.includedTrackedOrders} tracked orders a month` : ''}`}
            divider={i > 0}
          >
            {on && <Tag tone="ok">On your plan</Tag>}
            <button
              type="button"
              onClick={() => ask(a.key)}
              disabled={busy}
              className={on ? buttonClass.secondary : buttonClass.primary}
              aria-label={`${on ? 'Remove' : 'Add'} ${a.name}`}
            >
              {on ? <XIcon size={16} /> : <PlusIcon size={16} />}
              {on ? 'Remove' : 'Add'}
            </button>
          </SettingRow>
        );
      })}
      {p && (
        <PreviewPanel label={pending.label}>
          {p.changed && p.trial ? (
            <p data-testid="add-on-preview">
              Nothing is charged during your free trial.
              {p.nextInvoiceCents != null
                && ` Your first bill${p.nextBillAt ? `, on ${formatDate(p.nextBillAt)},` : ''} is ${formatMoney(p.nextInvoiceCents)}.`}
            </p>
          ) : p.changed ? (
            <p data-testid="add-on-preview">
              {p.prorationCents >= 0
                ? `${formatMoney(p.prorationCents)} for the rest of this period is added to your next bill`
                : `A credit of ${formatMoney(-p.prorationCents)} for the rest of this period comes off your next bill`}
              {p.nextBillAt ? ` on ${formatDate(p.nextBillAt)}` : ''}.
              {p.nextInvoiceCents != null && ` Your next bill: ${formatMoney(p.nextInvoiceCents)}.`}
            </p>
          ) : (
            <p>Nothing changes.</p>
          )}
          <div className="mt-2 flex flex-wrap gap-2">
            {p.changed && (
              <button type="button" onClick={confirm} disabled={busy} className={buttonClass.primary}>
                {busy ? 'Saving…' : 'Confirm change'}
              </button>
            )}
            <button type="button" onClick={() => setPending(null)} disabled={busy} className={buttonClass.secondary}>
              Cancel
            </button>
          </div>
        </PreviewPanel>
      )}
      {error && <Alert>{error}</Alert>}
    </SettingsCard>
  );
}

function IntervalToggle({ value, onChange }) {
  const segment = (v, label) => (
    <button
      key={v}
      type="button"
      role="radio"
      aria-checked={value === v}
      onClick={() => onChange(v)}
      className={`seg-item ${value === v ? 'seg-item-active' : ''}`}
    >
      {label}
    </button>
  );
  return (
    <div role="radiogroup" aria-label="Billing interval" className="seg self-start">
      {segment('month', 'Monthly')}
      {segment('year', 'Yearly · two months free')}
    </div>
  );
}

function PlanPicker({ data, reactivate, autoFocus }) {
  const { catalog } = data;
  // A shop granted the founding offer (B6) starts on its plan and interval: Core yearly.
  const founding = data.founding && data.founding.eligible && catalog.founding ? catalog.founding : null;
  const [interval, setBillingInterval] = useState(founding ? founding.interval : 'month');
  const [plan, setPlan] = useState(founding ? founding.plan : 'core');
  const [addOns, setAddOns] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // "Have a code?": the typed code, and what Stripe's preview says it takes off.
  const [codeText, setCodeText] = useState('');
  const [codePreview, setCodePreview] = useState(null);
  const [codeError, setCodeError] = useState('');
  const [codeBusy, setCodeBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (autoFocus && ref.current) ref.current.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
  }, [autoFocus]);

  // A preview prices one plan: a different plan, add-on or interval needs a new one.
  useEffect(() => { setCodePreview(null); setCodeError(''); }, [plan, interval, addOns]);

  const choosePlan = (key) => {
    setPlan(key);
    setAddOns((keys) => addOnsForPlan(catalog, key, keys));
  };

  const foundingOff = foundingDiscountCents(data, plan, interval);
  const total = totalCents(catalog, plan, addOns, interval) - foundingOff;
  const savings = yearlySavingsCents(catalog, plan, addOns);
  // The server decides who is switching from Hana (GET /billing hanaSwitch).
  const hana = data.hanaSwitch && data.hanaSwitch.switcher ? data.hanaSwitch : null;
  // Checkout takes one discount: the founding offer leaves no room for a code.
  const codesOpen = foundingOff <= 0;
  const typedCode = codesOpen ? codeText.trim() : '';

  const applyCode = async () => {
    if (!typedCode) return;
    setCodeBusy(true);
    setCodeError('');
    setCodePreview(null);
    try {
      const r = await subscriptionApi.previewCode({ code: typedCode, plan, interval, addOns });
      setCodePreview(r?.data || null);
    } catch (err) {
      setCodeError(errorText(err, "Couldn't check that code. Try again."));
    } finally {
      setCodeBusy(false);
    }
  };

  const checkout = async () => {
    setBusy(true);
    setError('');
    try {
      const r = await subscriptionApi.checkout({
        plan, interval, addOns, ...(foundingOff > 0 ? { founding: true } : {}), ...(typedCode ? { code: typedCode } : {}),
      });
      const url = r?.data?.url;
      if (!url) throw new Error('No checkout URL');
      window.location.assign(url);
    } catch (err) {
      const codeProblem = /^CODE_/.test(String(err?.data?.code || ''));
      setError(codeProblem ? err.message : "Couldn't open checkout. Try again, or contact us.");
      setBusy(false);
    }
  };

  return (
    <div ref={ref} id="choose-plan" className="col-span-full">
      <SettingsCard
        title={reactivate ? 'Reactivate your shop' : 'Choose a plan'}
        description={reactivate
          ? 'Pick a plan to reopen your shop. Everything is back as soon as the payment goes through.'
          : 'Prices are before tax. Change your plan here later; cancel from Manage card & invoices.'}
      >
        <IntervalToggle value={interval} onChange={setBillingInterval} />

        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1.5 text-xs text-gray-700">Plan</legend>
          {catalog.plans.map((p) => (
            <label
              key={p.key}
              className={`flex cursor-pointer items-center justify-between gap-3 rounded-md border px-4 py-3 transition-colors duration-[120ms] ${
                plan === p.key ? 'border-floral-700 bg-floral-50' : 'border-gray-300 bg-white hover:bg-floral-50'
              }`}
            >
              <span className="flex items-center gap-3">
                <input type="radio" name="plan" value={p.key} checked={plan === p.key} onChange={() => choosePlan(p.key)} className="accent-floral-700" />
                <span className="text-sm font-semibold text-gray-900">{p.name}</span>
                {p.includedTrackedOrders > 0 && (
                  <span className="text-[13px] text-gray-600">{p.includedTrackedOrders.toLocaleString('en-US')} tracked orders a month</span>
                )}
              </span>
              <span className="text-sm text-gray-900">
                {formatMoney(itemPriceCents(catalog, p.key, interval))}/{perInterval(interval)}
              </span>
            </label>
          ))}
        </fieldset>

        {availableAddOns(catalog, plan).length > 0 && (
          <fieldset className="flex flex-col gap-2">
            <legend className="mb-1.5 text-xs text-gray-700">Add-ons (optional)</legend>
            {availableAddOns(catalog, plan).map((a) => (
              <label key={a.key} className="flex cursor-pointer items-center justify-between gap-3 rounded-md border border-gray-300 bg-white px-4 py-2.5 transition-colors duration-[120ms] hover:bg-floral-50">
                <span className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={addOns.includes(a.key)}
                    onChange={() => setAddOns((keys) => toggleAddOn(catalog, keys, a.key))}
                    className="accent-floral-700"
                  />
                  <span className="text-[13px] text-gray-900">{a.name}</span>
                </span>
                <span className="text-[13px] text-gray-700">
                  {formatMoney(itemPriceCents(catalog, a.key, interval))}/{perInterval(interval)}
                </span>
              </label>
            ))}
          </fieldset>
        )}

        {codesOpen && (
          <div className="flex flex-col gap-1.5">
            <Field label="Have a code?" htmlFor="plan-code">
              <div className="flex flex-wrap items-center gap-2">
                <input
                  id="plan-code"
                  type="text"
                  autoComplete="off"
                  value={codeText}
                  onChange={(e) => { setCodeText(e.target.value); setCodePreview(null); setCodeError(''); }}
                  className={`${inputClass} max-w-xs flex-1`}
                />
                <button type="button" onClick={applyCode} disabled={!typedCode || codeBusy} className={buttonClass.secondary}>
                  {codeBusy ? 'Checking…' : 'Apply'}
                </button>
              </div>
            </Field>
            {codePreview && (
              <p className="text-[13px] text-gray-700" data-testid="picker-code">
                {codePreview.code}: {codePreview.summary}.
                {codePreview.discountCents > 0 && ` ${formatMoney(codePreview.discountCents)} off your first bill, applied at checkout.`}
              </p>
            )}
            {codeError && <Alert>{codeError}</Alert>}
            {!typedCode && data.pendingCode && (
              <p className="text-[13px] text-gray-700" data-testid="picker-pending-code">
                Code {data.pendingCode.code} will be applied at checkout.
              </p>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-3.5">
          <div>
            <p className="text-sm font-semibold text-gray-900" data-testid="picker-total">
              {formatMoney(total)}/{perInterval(interval)}
            </p>
            <p className="text-[13px] text-gray-600" data-testid="picker-savings">
              {interval === 'year'
                ? `You save ${formatMoney(savings)} a year compared with paying monthly.`
                : `Pay yearly and save ${formatMoney(savings)} a year.`}
            </p>
            {foundingOff > 0 && (
              <p className="text-[13px] text-gray-700" data-testid="picker-founding">
                Founding offer: {formatMoney(foundingOff)} off Core yearly, held for you.
              </p>
            )}
            {hana && (
              <p className="text-[13px] text-gray-700" data-testid="picker-hana-fee">
                One-time Hana import fee — waived on yearly plans: {interval === 'year' ? 'waived' : formatMoney(hana.feeCents)}
              </p>
            )}
          </div>
          <button type="button" onClick={checkout} disabled={busy} className={buttonClass.primary}>
            {busy ? 'Opening checkout…' : reactivate ? 'Reactivate' : 'Continue to checkout'}
          </button>
        </div>
        {error && <Alert>{error}</Alert>}
      </SettingsCard>
    </div>
  );
}

const intervalWord = (interval) => (interval === 'year' ? 'yearly' : 'monthly');

/** What a switch preview (POST /billing/switch/preview) means, in words. */
function SwitchPreviewText({ quote, currentName }) {
  const q = quote;
  const name = q.plan ? q.plan.name : '';
  const lines = [];
  if (q.kind === 'now') {
    lines.push(`${formatMoney(q.dueNowCents)} is charged to your card today: ${name} billed yearly${q.addOns && q.addOns.length ? ' with your add-ons' : ''}, less the unused part of this month's payment.`
      + (q.nextBillAt ? ` It renews on ${formatDate(q.nextBillAt)}.` : ''));
  } else if (q.kind === 'next_bill') {
    lines.push(`You move to ${name} now. `
      + (q.prorationCents >= 0
        ? `${formatMoney(q.prorationCents)} for the rest of this period is added to your next bill`
        : `A credit of ${formatMoney(-q.prorationCents)} for the rest of this period comes off your next bill`)
      + (q.nextBillAt ? ` on ${formatDate(q.nextBillAt)}` : '') + '.'
      + (q.nextInvoiceCents != null ? ` Your next bill: ${formatMoney(q.nextInvoiceCents)}.` : ''));
  } else if (q.kind === 'period_end') {
    lines.push(`Nothing changes until ${formatDate(q.effectiveAt)}. Then you move to ${name}, billed monthly, and your first monthly bill is ${formatMoney(q.nextInvoiceCents)}.`);
  } else if (q.kind === 'trial') {
    lines.push(`You move to ${name}, billed ${intervalWord(q.interval)}, now. Nothing is charged during your trial`
      + (q.nextBillAt ? `; your first bill, on ${formatDate(q.nextBillAt)}, is ${formatMoney(q.nextInvoiceCents)}.` : '.'));
  } else if (q.kind === 'keep') {
    lines.push(`Your scheduled switch will be canceled. You stay on ${currentName}.`);
  }
  if (q.hanaSwitchCents > 0) {
    lines.push(`That includes the ${formatMoney(q.hanaSwitchCents)} switching-from-Hana fee, which is waived only on yearly plans.`);
  }
  if (q.droppedAddOns && q.droppedAddOns.length > 0) {
    lines.push(`${q.droppedAddOns.map((a) => a.name).join(' and ')} ${q.droppedAddOns.length === 1 ? 'comes' : 'come'} with ${name}, so ${q.droppedAddOns.length === 1 ? 'it comes' : 'they come'} off your add-ons.`);
  }
  return (
    <>
      {lines.map((l) => <p key={l} data-testid="switch-preview">{l}</p>)}
      {q.foundingGained && (
        <p className="flex items-start gap-1.5 text-gray-800" data-testid="switch-founding-gained">
          <GiftIcon size={16} className="mt-0.5 flex-shrink-0 text-floral-700" />
          Your founding offer is taken with this switch: the price above includes it.
        </p>
      )}
      {q.foundingLost && (
        <p className="flex items-start gap-1.5 text-gray-800" data-testid="switch-founding-lost">
          <AlertTriangleIcon size={16} className="mt-0.5 flex-shrink-0" />
          You lose your founding price, which is for Core billed yearly only.
        </p>
      )}
    </>
  );
}

function SwitchPlanCard({ data, onChanged }) {
  const { subscription: sub, catalog } = data;
  const currentPlan = sub.plan && sub.plan.key;
  const currentName = `${sub.plan ? sub.plan.name : 'your plan'}, billed ${intervalWord(sub.interval)}`;
  const [interval, setBillingInterval] = useState(sub.interval);
  const [plan, setPlan] = useState(currentPlan);
  const [scheduled, setScheduled] = useState(null);
  const [quote, setQuote] = useState(null); // { target, preview }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const loadScheduled = useCallback(async () => {
    try {
      const r = await subscriptionApi.pendingSwitch();
      setScheduled(r?.data?.pending || null);
    } catch (_) { /* the switch still works; the notice just isn't shown */ }
  }, []);
  useEffect(() => { loadScheduled(); }, [loadScheduled]);

  const ask = async (target) => {
    setBusy(true);
    setError('');
    try {
      const r = await subscriptionApi.previewSwitch(target);
      setQuote({ target, preview: r.data });
    } catch (err) {
      setQuote(null);
      setError(errorText(err, "Couldn't price that switch. Try again."));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    setError('');
    try {
      const r = await subscriptionApi.switchPlan(quote.target, quote.preview.expectedCents, quote.preview.prorationDate);
      setQuote(null);
      setPlan(currentPlan);
      setBillingInterval(sub.interval);
      await loadScheduled();
      onChanged(r?.data?.billing || null);
    } catch (err) {
      if (err?.data?.code === 'PREVIEW_CHANGED') setQuote(null);
      setError(errorText(err, "Couldn't switch your plan. Try again."));
    } finally {
      setBusy(false);
    }
  };

  const options = switchPlanOptions(catalog, currentPlan);
  const keys = (sub.addOns || []).map((a) => a.key);
  const carried = addOnsForPlan(catalog, plan, keys)
    .map((k) => ({ key: k, quantity: ((sub.addOns || []).find((a) => a.key === k) || {}).quantity || 1 }));
  const unchanged = plan === currentPlan && interval === sub.interval;
  const p = quote && quote.preview;
  // A grant (QA B P1-8) waits for a switch to Core yearly; a shop already on
  // Core yearly gets it on its next yearly renewal (decided 2026-10-17).
  const foundingHeld = data.founding && data.founding.eligible && catalog.founding ? catalog.founding : null;
  const onFoundingPlan = foundingHeld && currentPlan === foundingHeld.plan && sub.interval === foundingHeld.interval;

  return (
    <div className="col-span-full">
      <SettingsCard
        title="Change your plan"
        description="Monthly to yearly is charged now. Yearly to monthly starts when your paid year ends. Prices are before tax."
      >
        {scheduled && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-gray-200 bg-white px-4 py-3 text-[13px] text-gray-800" data-testid="switch-scheduled">
            <span>
              On {formatDate(scheduled.effectiveAt)} you move to {scheduled.plan ? scheduled.plan.name : 'your new plan'}, billed {intervalWord(scheduled.interval)}.
            </span>
            <button
              type="button"
              onClick={() => ask({ plan: currentPlan, interval: sub.interval })}
              disabled={busy}
              className={buttonClass.secondary}
            >
              Keep my current plan
            </button>
          </div>
        )}

        {foundingHeld && (
          <p className="flex items-center gap-1.5 text-[13px] text-gray-800" data-testid="switch-founding-held">
            <GiftIcon size={16} className="flex-shrink-0 text-floral-700" />
            {onFoundingPlan
              ? `Founding offer: ${formatMoney(foundingHeld.amountOffCents)} off your next yearly renewal${sub.currentPeriodEnd ? ` on ${formatDate(sub.currentPeriodEnd)}` : ''}.`
              : `Founding offer: ${formatMoney(foundingHeld.amountOffCents)} off Core yearly, held for you. It applies when you switch to Core billed yearly.`}
          </p>
        )}

        <IntervalToggle value={interval} onChange={(v) => { setBillingInterval(v); setQuote(null); }} />

        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1.5 text-xs text-gray-700">Plan</legend>
          {options.map((o) => (
            <label
              key={o.key}
              className={`flex cursor-pointer items-center justify-between gap-3 rounded-md border px-4 py-3 transition-colors duration-[120ms] ${
                plan === o.key ? 'border-floral-700 bg-floral-50' : 'border-gray-300 bg-white hover:bg-floral-50'
              }`}
            >
              <span className="flex items-center gap-3">
                <input
                  type="radio"
                  name="switch-plan"
                  value={o.key}
                  checked={plan === o.key}
                  onChange={() => { setPlan(o.key); setQuote(null); }}
                  className="accent-floral-700"
                />
                <span className="text-sm font-semibold text-gray-900">{o.name}</span>
                {o.key === currentPlan && <Tag tone="ok">Your plan</Tag>}
              </span>
              <span className="text-sm text-gray-900">
                {formatMoney(itemPriceCents(catalog, o.key, interval))}/{perInterval(interval)}
              </span>
            </label>
          ))}
        </fieldset>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-3.5">
          <div>
            <p className="text-sm font-semibold text-gray-900" data-testid="switch-total">
              {formatMoney(totalCents(catalog, plan, carried, interval))}/{perInterval(interval)}
            </p>
            <p className="text-[13px] text-gray-600">
              {carried.length > 0 ? 'With your add-ons.' : 'Your add-ons carry over.'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => ask({ plan, interval })}
            disabled={busy || unchanged}
            className={buttonClass.primary}
          >
            {busy && !quote ? 'Pricing…' : 'Review change'}
          </button>
        </div>

        {p && (
          <PreviewPanel label={p.kind === 'keep' ? 'Keep your current plan' : `Switch to ${p.plan.name}, billed ${intervalWord(p.interval)}`}>
            <SwitchPreviewText quote={p} currentName={currentName} />
            <div className="mt-2 flex flex-wrap gap-2">
              <button type="button" onClick={confirm} disabled={busy} className={buttonClass.primary}>
                {busy ? 'Saving…'
                  : p.kind === 'now' ? `Pay ${formatMoney(p.dueNowCents)} and switch`
                    : p.kind === 'keep' ? 'Keep my current plan' : 'Confirm switch'}
              </button>
              <button type="button" onClick={() => setQuote(null)} disabled={busy} className={buttonClass.secondary}>
                Cancel
              </button>
            </div>
          </PreviewPanel>
        )}
        {error && <Alert>{error}</Alert>}
      </SettingsCard>
    </div>
  );
}

function CancelYearlyCard({ onCanceled }) {
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const ask = async () => {
    setBusy(true);
    setError('');
    try {
      const r = await subscriptionApi.cancelYearlyQuote();
      setQuote(r.data);
    } catch (err) {
      setError(errorText(err, "Couldn't work out your refund. Try again."));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    setError('');
    try {
      await subscriptionApi.cancelYearly(quote.refundCents);
      setQuote(null);
      onCanceled();
    } catch (err) {
      if (err?.data?.code === 'REFUND_CHANGED') setQuote(null);
      setError(errorText(err, "Couldn't cancel your plan. Try again."));
      setBusy(false);
    }
  };

  return (
    <SettingsCard title="Cancel your yearly plan" description="You get back what you paid, less each month started at the monthly price.">
      {!quote && (
        <button type="button" onClick={ask} disabled={busy} className={`${buttonClass.secondary} self-start`}>
          {busy ? 'Working it out…' : 'See my refund'}
        </button>
      )}
      {quote && (
        <PreviewPanel label="If you cancel now">
          <p data-testid="cancel-refund">
            You get {formatMoney(quote.refundCents)} back.
            {' '}You paid {formatMoney(quote.amountPaidCents)}; {quote.monthsStarted === 1 ? '1 month used comes' : `${quote.monthsStarted} months used come`} to {formatMoney(quote.usedCents)}
            {quote.perksCents > 0 ? `, and ${formatMoney(quote.perksCents)} of yearly perks are taken back` : ''}.
          </p>
          <p className="flex items-start gap-1.5 text-gray-800">
            <AlertTriangleIcon size={16} className="mt-0.5 flex-shrink-0" />
            Your shop closes as soon as you cancel. Your data is kept.
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <button type="button" onClick={confirm} disabled={busy} className={buttonClass.primary}>
              {busy ? 'Canceling…' : 'Cancel my plan'}
            </button>
            <button type="button" onClick={() => setQuote(null)} disabled={busy} className={buttonClass.secondary}>
              Keep my plan
            </button>
          </div>
        </PreviewPanel>
      )}
      {error && <Alert>{error}</Alert>}
    </SettingsCard>
  );
}

/**
 * What the page says back from Checkout (QA B P2-11): a plan chosen during
 * the free trial charged nothing, so it never says "your payment went through".
 */
export function checkoutResultText({ waiting, sub, trialEndsAt }) {
  if (waiting) return 'Thanks. Waiting for Stripe to confirm your plan…';
  if (sub && sub.status === 'trialing') {
    const date = formatDate(sub.currentPeriodEnd || trialEndsAt);
    return `Your plan is set. Nothing is charged until your free trial ends${date ? ` on ${date}` : ''}.`;
  }
  return 'Thanks, your payment went through.';
}

export default function PlanTab({ billingOnly = false, onLoaded = null }) {
  const { user, refreshSession } = useAuth();
  const owner = isOwner(user);
  const [searchParams] = useSearchParams();
  const billingResult = searchParams.get('billing');
  const choose = searchParams.get('choose') === '1';
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);
  const [portalError, setPortalError] = useState('');
  const [payBusy, setPayBusy] = useState(false);
  const [payError, setPayError] = useState('');
  const [waiting, setWaiting] = useState(billingResult === 'success');

  const load = useCallback(async () => {
    try {
      const r = await subscriptionApi.get();
      setData(r?.data || null);
      setFailed(!r?.data);
      return r?.data || null;
    } catch (_) {
      setFailed(true);
      return null;
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (data && onLoaded) onLoaded(data);
  }, [data, onLoaded]);

  // Back from Checkout: the webhook turns the subscription on shortly after.
  // Re-read until it shows (a billing-only session refreshes itself instead:
  // once the shop is active again, the full app opens).
  useEffect(() => {
    if (billingResult !== 'success') return undefined;
    let tries = 0;
    let stopped = false;
    let timer;
    const tick = async () => {
      tries += 1;
      let done = false;
      if (billingOnly) {
        try {
          const next = refreshSession ? await refreshSession() : null;
          done = Boolean(next && !next.billingOnly);
        } catch (_) { /* keep trying */ }
      } else {
        const d = await load();
        done = Boolean(d && d.subscription && LIVE.includes(d.subscription.status) && d.shopStatus !== 'suspended');
      }
      if (stopped) return;
      if (done || tries >= POLL_TRIES) {
        setWaiting(false);
        return;
      }
      timer = setTimeout(tick, POLL_MS);
    };
    timer = setTimeout(tick, billingOnly ? POLL_MS : 0);
    return () => { stopped = true; clearTimeout(timer); };
  }, [billingResult, billingOnly, load, refreshSession]);

  const openPortal = async () => {
    setPortalBusy(true);
    setPortalError('');
    try {
      const r = await subscriptionApi.portal();
      if (!r?.data?.url) throw new Error('No portal URL');
      window.location.assign(r.data.url);
    } catch (_) {
      setPortalError("Couldn't open the billing portal. Try again.");
      setPortalBusy(false);
    }
  };

  // The unpaid bill on Stripe's own page (QA B P1-4).
  const payNow = async () => {
    setPayBusy(true);
    setPayError('');
    try {
      const r = await subscriptionApi.payNow();
      if (!r?.data?.url) throw new Error('No invoice URL');
      window.location.assign(r.data.url);
    } catch (err) {
      setPayError(err?.data?.code === 'NO_OPEN_INVOICE'
        ? "There's no unpaid bill right now. If your card was declined, update it with Update card."
        : "Couldn't open the bill. Try again.");
      setPayBusy(false);
    }
  };

  // A yearly cancel closes the shop: trade this session for a billing-only one.
  const afterCancel = async () => {
    try {
      if (refreshSession) await refreshSession();
    } catch (_) { /* the next call signs out instead */ }
    load();
  };

  if (failed && !data) {
    return (
      <SettingsGrid>
        <SettingsCard title="Your plan">
          <p className="text-[13px] text-gray-700">Couldn&apos;t load your plan and billing. Refresh to try again.</p>
          <ContactLink />
        </SettingsCard>
      </SettingsGrid>
    );
  }
  if (!data) {
    return (
      <SettingsGrid>
        <SettingsCard title="Your plan">
          <p className="text-[13px] text-gray-600">Loading…</p>
        </SettingsCard>
      </SettingsGrid>
    );
  }
  if (data.billingExempt) return null;

  const sub = data.subscription;
  const live = Boolean(sub && LIVE.includes(sub.status));
  const suspended = data.shopStatus === 'suspended';
  const showPicker = owner && !live && !data.assignedPlan && Boolean(data.catalog);
  const showAddOns = owner && live && !suspended && !sub.cancelAtPeriodEnd && Boolean(data.catalog);
  const showSwitch = showAddOns && sub.status !== 'past_due' && Boolean(sub.plan);
  const showCancelYearly = owner && live && !suspended && sub.interval === 'year' && !sub.cancelAtPeriodEnd;

  return (
    <SettingsGrid>
      {billingResult === 'success' && (
        <div className="col-span-full rounded-md border border-gray-200 bg-white px-5 py-3 text-[13px] text-gray-800" role="status" data-testid="billing-result">
          {checkoutResultText({ waiting, sub, trialEndsAt: data.trialEndsAt })}
          {!waiting && billingOnly && ' If your shop has not reopened yet, refresh this page in a minute.'}
        </div>
      )}
      {billingResult === 'cancel' && (
        <div className="col-span-full rounded-md border border-gray-200 bg-white px-5 py-3 text-[13px] text-gray-800" role="status" data-testid="billing-result">
          Checkout was canceled. Nothing was charged.
        </div>
      )}
      <YourPlanCard
        data={data}
        owner={owner}
        onPortal={openPortal}
        portalBusy={portalBusy}
        portalError={portalError}
        onPayNow={payNow}
        payBusy={payBusy}
        payError={payError}
      />
      <UsageCard usage={data.usage} rateCents={data.catalog ? data.catalog.overageCentsPerTrackedOrder : 0} />
      {showSwitch && <SwitchPlanCard key={`${sub.plan.key}-${sub.interval}`} data={data} onChanged={(next) => (next ? setData(next) : load())} />}
      {showAddOns && <AddOnsCard data={data} onChanged={(next) => (next ? setData(next) : load())} />}
      {showCancelYearly && <CancelYearlyCard onCanceled={afterCancel} />}
      {showPicker && <PlanPicker data={data} reactivate={suspended} autoFocus={choose} />}
    </SettingsGrid>
  );
}
